import { t } from './i18n.js';

// Market metadata comes from the server (/api/markets); this module keeps a registry for formatting.
const KNOWN_COLORS = {
  BTC: '#F7931A', ETH: '#7B8CE8', SOL: '#14C8A0', BNB: '#F3BA2F', XRP: '#9AA6B8', DOGE: '#C2A633', ADA: '#3468D1',
  LTC: '#A6A9AA', BCH: '#8DC351', XMR: '#FF6600', DASH: '#008CE7', ZEC: '#ECB244', ETC: '#33A06F', XLM: '#B7C3D6',
  XAU: '#E8B04A', XAG: '#C0C7D1', XPT: '#9FB3C8', XPD: '#B7A3C4', PAXG: '#D9A93A', COPPER: '#C46A3A',
  WTI: '#5C6B7A', BRENT: '#4E6E81', NATGAS: '#5AA6E0',
  TSLA: '#E31937', NVDA: '#76B900', AAPL: '#A2AAAD', MSFT: '#00A4EF', AMZN: '#FF9900', GOOGL: '#4285F4', META: '#0866FF',
  NFLX: '#E50914', AMD: '#ED1C24', COIN: '#0052FF', MSTR: '#FF7A00', SPY: '#8B9CFF', QQQ: '#14C8A0',
};
const PALETTE = ['#8B9CFF', '#14C8A0', '#E8B04A', '#E86F9B', '#5AA6E0', '#B48CF2', '#F28B5B', '#7FD17F'];

const registry = new Map();
export let CATEGORIES = [];
export function setMarkets({ markets, categories }) {
  registry.clear();
  for (const m of markets) registry.set(m.symbol, m);
  CATEGORIES = categories;
}
export const allMarkets = () => [...registry.values()];

function colorFor(base) {
  if (KNOWN_COLORS[base]) return KNOWN_COLORS[base];
  let h = 0;
  for (const ch of base) h = (h * 31 + ch.charCodeAt(0)) >>> 0;
  return PALETTE[h % PALETTE.length];
}

export const meta = (s) => {
  const m = registry.get(s);
  const base = m ? m.base : String(s || '').replace(/USDT$/, '');
  return {
    symbol: s, base, quote: m ? m.quote : 'USDT', name: m && m.label ? m.label : `${base}/${m ? m.quote : 'USDT'}`, short: m && m.label ? m.label : base,
    fullName: m ? m.name : base, category: m ? m.category : 'crypto', minQty: m ? m.minQty : 0,
    color: colorFor(base), qtyStep: m ? m.qtyStep : 0.001, qtyDp: m ? m.qtyDp : 3, priceDp: m ? m.priceDp : 2,
    maxLeverage: m ? m.maxLeverage : 10, feeRate: m ? m.feeRate : null,
  };
};

// USD value of one unit of a market's quote currency (1 for USDT markets; converts JPY, GBP… for forex).
export function usdPerQuote(symbol, prices) {
  const m = registry.get(symbol);
  if (!m) return 1;
  const q = m.quote;
  const px = (s) => (prices[s] ? prices[s].price : null);
  if (q === 'USD' || q === 'USDT' || q === 'USDC') return 1;
  if (m.base === 'USD') return px(symbol) ? 1 / px(symbol) : null;
  if (registry.has(q + 'USD')) return px(q + 'USD');
  if (registry.has('USD' + q)) return px('USD' + q) ? 1 / px('USD' + q) : null;
  return null;
}

export function num(v, dp = 2) {
  if (v == null || !Number.isFinite(Number(v))) return '—';
  return Number(v).toLocaleString('en-US', { minimumFractionDigits: dp, maximumFractionDigits: dp });
}
export const usd = (v, dp = 2) => (v == null ? '—' : (v < 0 ? '-$' : '$') + num(Math.abs(v), dp));
export const signedUsd = (v) => (v == null ? '—' : (v > 0 ? '+' : v < 0 ? '-' : '') + '$' + num(Math.abs(v), 2));
export const pct = (v, dp = 2) => (v == null ? '—' : (v > 0 ? '+' : '') + num(v, dp) + '%');
export const price = (s, v) => num(v, meta(s).priceDp);
export const shortAddr = (a) => (a ? `${a.slice(0, 6)}…${a.slice(-4)}` : '');
export const pnlClass = (v) => (v > 0 ? 'up' : v < 0 ? 'down' : '');

export function compact(v) {
  if (v == null) return '—';
  const a = Math.abs(v);
  if (a >= 1e9) return '$' + num(v / 1e9, 2) + 'B';
  if (a >= 1e6) return '$' + num(v / 1e6, 2) + 'M';
  if (a >= 1e3) return '$' + num(v / 1e3, 1) + 'K';
  return '$' + num(v, 0);
}

export function countdown(iso) {
  const ms = new Date(iso).getTime() - Date.now();
  if (!(ms > 0)) return t('now');
  const h = Math.floor(ms / 3600e3), m = Math.floor((ms % 3600e3) / 60e3);
  return `${h}h ${String(m).padStart(2, '0')}m`;
}

export const STAGE_LABEL = {
  get stage_1() { return t('Stage 1'); },
  get stage_2() { return t('Stage 2'); },
  get funded() { return t('Stage 3'); },
};
